import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query';
import type {
  ChecklistView,
  ChecklistItemInput,
} from '@internal/backstage-plugin-bidone-backend';
import { useAuthedFetch } from '../useAuthedFetch';
import { RELEASES_KEY, checklistKey } from './keys';

type CloneArgs = { release: string; newRelease: string };
type CloneResult = { source: string; dest: string; items: number };
type DeleteArgs = { release: string };
type SaveArgs = { release: string; items: ChecklistItemInput[] };

export const useCloneRelease = (): UseMutationResult<
  CloneResult,
  Error,
  CloneArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ release, newRelease }) =>
      authedFetch(`/releases/${encodeURIComponent(release)}/clone`, {
        method: 'POST',
        json: { newRelease },
      }),
    onSuccess: result => {
      // Optimistically add the new release to the dropdown's source list so
      // when the parent flips selection to it (synchronously, on the same
      // tick), the MUI Select doesn't warn about an out-of-range value.
      // The invalidate below still runs to pick up any concurrent additions.
      qc.setQueryData<string[] | undefined>(RELEASES_KEY, prev =>
        prev && !prev.includes(result.dest) ? [...prev, result.dest] : prev,
      );
      // Drop any stale cached checklist for the new partition name (could
      // exist if a release with the same name was deleted earlier in the
      // session) — otherwise the detail panel renders the stale empty data
      // until the refetch settles.
      qc.removeQueries({ queryKey: checklistKey(result.dest) });
      qc.invalidateQueries({ queryKey: RELEASES_KEY });
    },
  });
};

export const useDeleteChecklist = (): UseMutationResult<
  void,
  Error,
  DeleteArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ release }) => {
      await authedFetch(`/releases/${encodeURIComponent(release)}/checklist`, {
        method: 'DELETE',
      });
    },
    onSuccess: (_, { release }) => {
      qc.removeQueries({ queryKey: checklistKey(release) });
      qc.invalidateQueries({ queryKey: RELEASES_KEY });
    },
  });
};

export const useSaveChecklist = (): UseMutationResult<
  ChecklistView,
  Error,
  SaveArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ release, items }) =>
      authedFetch(`/releases/${encodeURIComponent(release)}/checklist`, {
        method: 'PUT',
        json: { items },
      }),
    onSuccess: (data, { release }) => {
      qc.setQueryData(checklistKey(release), data);
      qc.invalidateQueries({ queryKey: RELEASES_KEY });
    },
  });
};
